"use client";

import React, { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { twMerge } from "tailwind-merge";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  React.SelectHTMLAttributes<HTMLSelectElement>,
  "children"
> {
  error?: boolean;
  options?: readonly SelectOption[];
  placeholder?: string;
  children?: React.ReactNode;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className = "",
      disabled = false,
      error = false,
      options = [],
      placeholder = "Select an option",
      value,
      onChange,
      children,
      ...props
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<string>(
      typeof value === "string" ? value : "",
    );
    const [dropdownPosition, setDropdownPosition] = useState<"bottom" | "top">(
      "bottom",
    );
    const containerRef = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Handle native select for form submission
    const hiddenSelectRef = useRef<HTMLSelectElement>(null);

    useEffect(() => {
      setSelectedValue(typeof value === "string" ? value : "");
    }, [value]);

    // Detect dropdown position on open
    useEffect(() => {
      if (isOpen && triggerRef.current && dropdownRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const dropdownHeight = dropdownRef.current.offsetHeight;
        const spaceBelow = window.innerHeight - triggerRect.bottom;
        const spaceAbove = triggerRect.top;

        // If not enough space below and more space above, render upward
        if (
          spaceBelow < dropdownHeight + 8 &&
          spaceAbove > dropdownHeight + 8
        ) {
          setDropdownPosition("top");
        } else {
          setDropdownPosition("bottom");
        }
      }
    }, [isOpen]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
          document.removeEventListener("mousedown", handleClickOutside);
        };
      }
    }, [isOpen]);

    const handleSelect = (optionValue: string) => {
      setSelectedValue(optionValue);
      setIsOpen(false);

      // Trigger onChange event
      if (onChange) {
        const event = {
          target: { value: optionValue },
        } as React.ChangeEvent<HTMLSelectElement>;
        onChange(event);
      }

      // Update hidden select
      if (hiddenSelectRef.current) {
        hiddenSelectRef.current.value = optionValue;
      }
    };

    const selectedLabel =
      options.find((opt) => opt.value === selectedValue)?.label ||
      (children && selectedValue ? selectedValue : placeholder);

    const triggerClasses = twMerge(
      "h-11 w-full rounded-lg border px-4 text-sm shadow-theme-xs",
      "flex items-center justify-between transition-colors",
      "bg-white text-text-primary",
      "dark:bg-gray-900 dark:text-white/90",
      disabled
        ? "cursor-not-allowed border-gray-300 text-text-secondary dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400"
        : error
          ? "border-error-300 dark:border-error-500/50"
          : "border-gray-300 dark:border-gray-700",
      isOpen && !disabled
        ? "border-brand-300 dark:border-brand-800"
        : "hover:border-gray-400 dark:hover:border-gray-600",
      className,
    );

    const dropdownClasses = twMerge(
      "absolute left-0 right-0 rounded-lg border border-gray-300 bg-white shadow-lg z-50",
      "dark:border-gray-700 dark:bg-gray-900",
      "max-h-64 overflow-y-auto",
      dropdownPosition === "bottom" ? "top-full mt-2" : "bottom-full mb-2",
    );

    const optionClasses = (isSelected: boolean, isDisabled?: boolean) =>
      twMerge(
        "px-4 py-3 text-sm transition-colors",
        isDisabled
          ? "cursor-not-allowed text-gray-300 dark:text-gray-600 bg-gray-50/50 dark:bg-gray-800/20"
          : isSelected
            ? "cursor-pointer bg-accent text-text-primary font-medium"
            : "cursor-pointer text-text-secondary hover:bg-accent/25 hover:text-text-primary",
      );

    return (
      <div className="relative" ref={containerRef}>
        {/* Hidden native select for form submission */}
        <select
          ref={hiddenSelectRef || ref}
          value={selectedValue}
          onChange={(e) => handleSelect(e.target.value)}
          disabled={disabled}
          className="hidden"
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
          {children}
        </select>

        {/* Custom trigger button */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={triggerClasses}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span
            className={
              selectedValue
                ? "text-text-primary dark:text-white/90"
                : "text-gray-400"
            }
          >
            {selectedLabel}
          </span>
          <ChevronDownIcon
            className={twMerge(
              "h-4 w-4 text-text-secondary dark:text-gray-400 transition-transform",
              isOpen && "rotate-180",
            )}
            aria-hidden="true"
          />
        </button>

        {/* Custom dropdown menu */}
        {isOpen && !disabled && (
          <div className={dropdownClasses} role="listbox" ref={dropdownRef}>
            {options.length > 0 ? (
              options.map((opt) => (
                <div
                  key={opt.value}
                  role="option"
                  aria-selected={selectedValue === opt.value}
                  aria-disabled={opt.disabled}
                  onClick={() => !opt.disabled && handleSelect(opt.value)}
                  className={optionClasses(selectedValue === opt.value, opt.disabled)}
                >
                  {opt.label}
                </div>
              ))
            ) : (
              <div className="px-4 py-3 text-sm text-text-secondary ">
                No options available
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";

export default Select;
